import { Component, computed, input } from '@angular/core';
import { UwIcon } from '../icon/uw-icon';

export type UwButtonVariant =
  | 'primary'
  | 'accent'
  | 'ai'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'danger-outline'
  | 'success'
  | 'success-outline';

export type UwButtonSize = 'xs' | 'sm' | 'md';

@Component({
  selector: 'uw-button',
  standalone: true,
  imports: [UwIcon],
  template: `
    <button
      [attr.type]="type()"
      [disabled]="disabled() || loading()"
      [attr.aria-busy]="loading() ? 'true' : null"
      [class]="classes()"
    >
      @if (loading()) {
        <span class="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
      } @else if (icon()) {
        <uw-icon [name]="icon()" [size]="iconSize()" />
      }
      <ng-content />
      @if (suffixIcon()) {
        <uw-icon [name]="suffixIcon()" [size]="iconSize()" />
      }
    </button>
  `,
})
export class UwButton {
  readonly variant = input<UwButtonVariant>('secondary');
  readonly size = input<UwButtonSize>('md');
  readonly type = input<'button' | 'submit'>('button');
  readonly icon = input<string>('');
  readonly suffixIcon = input<string>('');
  readonly iconSize = input(14);
  readonly disabled = input(false);
  readonly loading = input(false);

  protected readonly classes = computed(() => {
    const base =
      'inline-flex items-center justify-center gap-1.5 font-semibold whitespace-nowrap select-none transition-colors cursor-pointer ' +
      'disabled:opacity-50 disabled:pointer-events-none';
    const sizes: Record<UwButtonSize, string> = {
      xs: 'px-2 py-0.5 text-[11px] rounded-md',
      sm: 'px-2.5 py-1 text-[11.5px] rounded-md',
      md: 'px-3.5 py-1.5 text-[12.5px] rounded-lg',
    };
    const variants: Record<UwButtonVariant, string> = {
      primary: 'bg-primary text-white hover:bg-primary-hover shadow-sm',
      accent: 'bg-accent text-white hover:bg-accent-hover shadow-sm',
      ai: 'bg-ai text-white hover:bg-ai-hover shadow-sm',
      secondary: 'bg-surface text-primary border border-line-strong hover:bg-surface-sunken shadow-sm',
      ghost: 'bg-transparent text-secondary hover:bg-surface-sunken hover:text-primary border border-transparent',
      danger: 'bg-danger text-white hover:opacity-90 shadow-sm',
      'danger-outline': 'bg-transparent text-danger border border-danger hover:bg-danger-soft',
      success: 'bg-success text-white hover:opacity-90 shadow-sm',
      'success-outline': 'bg-transparent text-success border border-success hover:bg-success-soft',
    };
    return [base, sizes[this.size()], variants[this.variant()]].join(' ');
  });
}
